import { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, InputGroup, Row, Table } from "react-bootstrap";
import { X as CloseIcon } from "react-bootstrap-icons";
import { useSessionStorage } from "usehooks-ts";
import {
  type ContentGetMiscContentFilteredRequest,
  type DocumentSubType,
  DocumentType,
  // Interrogatory type is correctly typed with required PKs
  type Interrogatory,
  type MiscContent,
  MiscContentType,
  PropoundingParty,
} from "@/briefpoint-client";
import type { QuestionListItems } from "@/components/DocumentWizard/PropoundDocumentReview";
import { DropDownFilterList } from "@/components/DropDownFilterList";
import { DropDownRadio } from "@/components/DropDownRadio";
import Loading from "@/components/Loading";
import { useContentApi, useTagApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import { NO_CASE_TEXT } from "@/lib/constants";
import type { CaseResponse, GroupedContent } from "@/lib/types";
import {
  caseTypeDisplay,
  getSortedTags,
  getTagsDisplay,
  getTagsKeys,
} from "@/lib/utils";
import CheckmarkListDiscReq from "./CheckmarkListDiscReq";
import styles from "./QuestionsTableDiscRequest.module.scss";

interface Props {
  questions: MiscContent[];
  filteredSourceIdRequests: Interrogatory[] | undefined;
  subType?: DocumentSubType;
  caseInfo?: CaseResponse;
  caseType?: string;
  checkedItems: QuestionListItems[];
  tagSectionToOpen?: string;
  tagIdToFind?: number;
  determineCheck: (id: string | undefined) => boolean | undefined;
  handleCheckboxChange: (
    id: string | undefined,
    checked: boolean,
    parentTag: string,
    content: string | null | undefined,
    question?: MiscContent,
  ) => void;
  setCheckedItems: React.Dispatch<React.SetStateAction<QuestionListItems[]>>;
}

export default function QuestionsTableDiscRequest({
  questions,
  filteredSourceIdRequests,
  subType,
  caseInfo,
  caseType,
  checkedItems,
  tagSectionToOpen,
  tagIdToFind,
  handleCheckboxChange,
  setCheckedItems,
}: Props) {
  // const [selectedQuestionSubType, setSelectedQuestionSubType] = useState<DocumentSubType>(tab ? Number(tab) : 1);
  // const [selectedQuestionSubType] = useState<DocumentSubType>(tab ? Number(tab) : 1);
  const [availableTags, setAvailableTags] = useState<
    Map<number, string> | undefined
  >();
  const [showDefaults, setShowDefaults] = useSessionStorage(
    "library-show-bp-defaults",
    true,
  );
  const [showQuestions, setShowQuestions] = useState<string[]>([]);
  const [expandAll, setExpandAll] = useState(false);
  const [defaultQuestions, setDefaultQuestions] = useState<MiscContent[]>();
  const [tagSearch, setTagSearch] = useState<string>("");
  const tagApi = useTagApi();
  const contentApi = useContentApi();
  const { jurisdictions } = useAuth();
  const ca_superior_court = (jurisdictions ?? [])[0];
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [classFilter, setClassFilter] = useState<string | null>(null);
  const [positionFilter, setPositionFilter] = useState<string | null>(null);

  const groupedQuestions = {} as GroupedContent;
  const allQuestions = useMemo(() => {
    return showDefaults && defaultQuestions
      ? questions.concat(defaultQuestions)
      : questions;
  }, [showDefaults, defaultQuestions, questions]);

  useEffect(() => {
    async function loadTags() {
      const tagsList = new Map<number, string>();
      const loadedTags = await tagApi.tagFindTags({});

      for (const t of loadedTags) {
        tagsList.set(t.id, t.name);
      }
      setAvailableTags(tagsList);
    }
    loadTags();
  }, [tagApi]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: handleClearEvent changes every render
  useEffect(() => {
    async function loadDefaults() {
      const query: ContentGetMiscContentFilteredRequest = {
        type: MiscContentType.DiscoveryQuestion,
        documentType: DocumentType.DiscoveryRequest,
        documentSubType: subType,
        caseType: caseType,
        skipTagFiltering: true,
      };
      const defaults =
        await contentApi.contentGetDefaultMiscContentFiltered(query);
      setDefaultQuestions(defaults);
      handleClearEvent();

      // Set positionFilter based on caseInfo.clientPosition AFTER handleClearEvent
      if (caseInfo?.clientPosition) {
        const positionValues = caseInfo.clientPosition
          .split(",")
          .map((item) => item.trim());
        if (positionValues.includes("Plaintiff")) {
          setPositionFilter("Plaintiff");
        } else if (positionValues.includes("Defendant")) {
          setPositionFilter("Defendant");
        } else {
          setPositionFilter(null);
        }
      }
    }

    loadDefaults();
  }, [
    contentApi,
    subType,
    caseType,
    tagSectionToOpen,
    caseInfo?.clientPosition,
    setShowDefaults,
  ]);

  let typeQuestions = allQuestions.filter(
    (x) => !subType || x.documentSubType === subType,
  );

  if (tagFilter.length > 0) {
    typeQuestions = typeQuestions.filter((tq) =>
      tagFilter.every((tf) => tq.tagIds?.includes(Number(tf))),
    );
  }

  typeQuestions = typeQuestions.filter((tq) => {
    if (positionFilter === null) {
      return true;
    }
    if (positionFilter === "Plaintiff") {
      // Include Defendant positions (opposite of Plaintiff) and unassigned positions
      return (
        tq.respondentType === PropoundingParty.Defendant ||
        tq.respondentType === undefined ||
        tq.respondentType === null
      );
    }
    if (positionFilter === "Defendant") {
      // Include Plaintiff positions (opposite of Defendant) and unassigned positions
      return (
        tq.respondentType === PropoundingParty.Plaintiff ||
        tq.respondentType === undefined ||
        tq.respondentType === null
      );
    }
    return false;
  });

  typeQuestions = typeQuestions.filter((tq) => {
    if (classFilter === null) {
      return true;
    }
    if (classFilter === "Individual" && tq.isForRepresentative === false) {
      return true;
    }
    if (classFilter === "Representative" && tq.isForRepresentative === true) {
      return true;
    }
    return false;
  });

  for (const q of typeQuestions) {
    const key = `${caseTypeDisplay(
      q.caseTypeId,
      ca_superior_court,
      true,
      NO_CASE_TEXT,
    )}${getTagsKeys(q.tagIds, availableTags)}${
      q.isForRepresentative !== undefined ? `-${q.isForRepresentative}` : ""
    }${q.respondentType !== undefined ? `-${q.respondentType}` : ""}${
      !!q.sharedWithFirmId && "-firm"
    }${!q.ownerId && "-bp"}`;
    if (!(key in groupedQuestions)) {
      groupedQuestions[key] = [];
    }
    groupedQuestions[key].push(q);
  }

  function handleClearEvent() {
    setTagSearch("");
    setTagFilter([]);
    setClassFilter(null);
    setPositionFilter(null);
    setExpandAll(false);
    setShowQuestions([]);
  }

  function openOrCloseQuestion(key: string) {
    setShowQuestions((q) => {
      const update = [...q];
      const existing = update.indexOf(key);
      if (existing > -1) {
        update.splice(existing, 1);
      } else {
        update.push(key);
      }

      return update;
    });
  }

  function handleExpandAllClick() {
    if (expandAll) {
      setExpandAll(false);
      setShowQuestions([]);
    } else {
      setExpandAll(true);
      setShowQuestions(Object.keys(groupedQuestions));
    }
  }

  // Checks if an existing question already exists/is selected
  function existingQuestion(id: string | undefined) {
    const isExisting = filteredSourceIdRequests?.find(
      (obj) => obj.sourceId === id,
    );
    return Boolean(isExisting);
  }

  function handleQuestionSearch(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.value !== "") {
      setExpandAll(true);
      setShowQuestions(Object.keys(groupedQuestions));
    } else {
      setExpandAll(false);
      setShowQuestions([]);
    }
    setTagSearch(e.target.value);
  }

  function handleClearSearch() {
    setTagSearch("");
    setExpandAll(false);
    setShowQuestions([]);
  }

  return (
    <div
      key="step-container"
      className={styles.stepContainer}
      style={{ gap: 0 }}
    >
      <div key="content" className="flex-grow-1">
        <Loading isLoading={!ca_superior_court || !availableTags}>
          <Row key="filters-row">
            <Col key="filters-actions" className={styles.filters}>
              <InputGroup>
                <Form.Control
                  id="search-input"
                  className="mb-4"
                  placeholder="Type to search..."
                  onChange={handleQuestionSearch}
                  size="sm"
                  value={tagSearch}
                />
                {tagSearch && (
                  <InputGroup.Text
                    className={`${styles.clearBtn} ${styles.search}`}
                    onClick={handleClearSearch}
                  >
                    <CloseIcon />
                  </InputGroup.Text>
                )}
              </InputGroup>
              <div className="d-flex align-items-center">
                <span className={styles.filterTitle}>Filter By:</span>
                <div className={styles.filterDropdownWrapper}>
                  {!!tagFilter.length && (
                    <label className={styles.filterLabel} htmlFor="tags-filter">
                      Tags
                    </label>
                  )}
                  <DropDownFilterList
                    key="tags-filter"
                    id="tags-filter"
                    text={
                      !tagFilter.length
                        ? "Tags"
                        : getTagsDisplay(tagFilter, availableTags)
                    }
                    itemsList={getSortedTags(
                      allQuestions,
                      availableTags,
                      tagFilter,
                    )}
                    activeFilter={tagFilter}
                    updateFilter={setTagFilter}
                  />
                </div>

                <div className={styles.filterDropdownWrapper}>
                  {positionFilter !== null && (
                    <label
                      className={styles.filterLabel}
                      htmlFor="position-filter"
                    >
                      Your Position
                    </label>
                  )}
                  <DropDownRadio
                    key="position-filter"
                    id="position-filter"
                    text={"Your Position"}
                    itemsList={[
                      { name: "Any", id: null, default: true },
                      { name: "Plaintiff", id: "Plaintiff" },
                      { name: "Defendant", id: "Defendant" },
                    ]}
                    activeFilter={positionFilter}
                    updateFilter={setPositionFilter}
                  />
                </div>

                <div className={styles.filterDropdownWrapper}>
                  {classFilter !== null && (
                    <label
                      className={styles.filterLabel}
                      htmlFor="class-filter"
                    >
                      Individual/Class
                    </label>
                  )}
                  <DropDownRadio
                    key="class-filter"
                    id="class-filter"
                    text="Individual/Class"
                    itemsList={[
                      { name: "Any", id: null, default: true },
                      { name: "Individual", id: "Individual" },
                      { name: "Representative", id: "Representative" },
                    ]}
                    activeFilter={classFilter}
                    updateFilter={setClassFilter}
                  />
                </div>
                {(tagFilter.length ||
                  positionFilter !== null ||
                  classFilter !== null) && (
                  <Button
                    className={`library-clear-btn ${styles.clearBtn}`}
                    variant="link"
                    onClick={handleClearEvent}
                  >
                    Clear All
                  </Button>
                )}
              </div>
            </Col>
            <Col key="filters-new" className="col-4">
              <Form.Check
                id="include-defaults-check"
                className={styles.formCheck}
                style={{ fontSize: "14px", display: "inline-block" }}
                checked={showDefaults}
                onChange={(e) => setShowDefaults(e.target.checked)}
                label="Show Briefpoint Library"
              />
            </Col>
          </Row>
          <Row className="mt-2">
            <Table style={{ width: "100%", overflow: "auto" }}>
              <thead>
                <tr className="table-header">
                  <th className="table-header-cell" scope="col" />
                  <th className="table-header-cell" scope="col" />
                  <th className="table-header-cell" scope="col">
                    Tags
                  </th>
                  <th className="table-header-cell" scope="col">
                    Resp. Position
                  </th>
                  <th className="table-header-cell" scope="col">
                    Representative
                  </th>
                  <th className="table-header-cell" scope="col" />
                  <th className="table-header-cell" scope="col">
                    # of Questions
                  </th>
                  <th className="table-header-cell" scope="col">
                    <Button
                      className={styles.expandAll}
                      variant="link"
                      onClick={handleExpandAllClick}
                    >
                      {!expandAll ? "Expand All" : "Collapse All"}
                    </Button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(groupedQuestions)
                  .filter((k) => {
                    if (!tagSearch) return true;
                    const searchRegex = new RegExp(
                      `\\b${tagSearch.toLowerCase()}`,
                      "i",
                    );
                    return groupedQuestions[k].some(
                      (q) =>
                        (q.content &&
                          searchRegex.test(q.content.toLowerCase())) ||
                        searchRegex.test(k.toLowerCase()),
                    );
                  })
                  .sort((a, b) => a.localeCompare(b))
                  .map((k, i) => {
                    const group = groupedQuestions[k].sort(
                      (a, b) => (a.order ?? 0) - (b.order ?? 0),
                    );
                    const question = group[0];

                    return (
                      <CheckmarkListDiscReq
                        key={i}
                        groupedQuestions={groupedQuestions}
                        index={i}
                        k={k}
                        openOrCloseQuestion={openOrCloseQuestion}
                        showQuestions={showQuestions}
                        caSupCourt={ca_superior_court}
                        availableTags={availableTags}
                        caseTypeDisplay={caseTypeDisplay}
                        question={question}
                        setShowQuestions={setShowQuestions}
                        checkedItems={checkedItems}
                        setCheckedItems={setCheckedItems}
                        tagSectionToOpen={tagSectionToOpen}
                        tagIdToFind={tagIdToFind}
                        group={group}
                        tagSearch={tagSearch}
                        existingQuestion={existingQuestion}
                        handleCheckboxChange={handleCheckboxChange}
                      />
                    );
                  })}
              </tbody>
            </Table>
          </Row>
        </Loading>
      </div>
    </div>
  );
}
